import { useState, useRef, useCallback, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';

interface TooltipProps {
  content: string;
  className?: string;
}

/**
 * Tooltip with a small ⓘ icon. Shows on hover (desktop) or tap (mobile).
 * Positions above by default, flips below if not enough space.
 */
export function Tooltip({ content, className = '' }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('top');
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // If not enough space above (tooltip height ~60px + arrow), show below
    setPosition(rect.top < 70 ? 'bottom' : 'top');
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  // Close on outside tap (mobile)
  useEffect(() => {
    if (!visible) return;
    const handleOutside = (e: TouchEvent | MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        tooltipRef.current && !tooltipRef.current.contains(e.target as Node)
      ) {
        setVisible(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [visible]);

  if (!content) return null;

  return (
    <span
      ref={triggerRef}
      className={`tw-relative tw-inline-flex tw-items-center tw-ml-1.5 ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onTouchStart={(e) => {
        e.preventDefault();
        visible ? hide() : show();
      }}
    >
      <HelpCircle className="tw-w-3.5 tw-h-3.5 tw-text-gray-400 hover:tw-text-gray-600 tw-cursor-help tw-transition-colors tw-pointer-events-auto" />

      {visible && (
        <div
          ref={tooltipRef}
          className={`tw-absolute tw-z-[9999] tw-w-64 tw-px-3 tw-py-2 tw-text-xs tw-leading-relaxed tw-text-white tw-bg-gray-900 tw-rounded-lg tw-shadow-lg tw-transition-opacity tw-duration-150 tw-pointer-events-none
            ${position === 'top' ? 'tw-bottom-full tw-mb-2' : 'tw-top-full tw-mt-2'}
            tw-left-1/2 -tw-translate-x-1/2`}
        >
          {content}
          {/* Arrow */}
          <div
            className={`tw-absolute tw-left-1/2 -tw-translate-x-1/2 tw-w-0 tw-h-0
              ${position === 'top'
                ? 'tw-top-full tw-border-t-[6px] tw-border-t-gray-900 tw-border-x-[6px] tw-border-x-transparent'
                : 'tw-bottom-full tw-border-b-[6px] tw-border-b-gray-900 tw-border-x-[6px] tw-border-x-transparent'
              }`}
          />
        </div>
      )}
    </span>
  );
}
